import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
// Utils
import { Form, FormElements } from '@zolteam/zol-react-form';
// Components
import { Box, Button } from '@zolteam/zol-admin-ui';
import Logo from '../../../assets/client_logo.svg?react';
// Services
import useParazol from '../../../services/parazol/useParazol';
// Locales
import fr from '../../../locales/fr.json';

const FORM_NAME = 'login';

const SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'Login',
  properties: {
    username: {
      type: 'string',
      format: 'email',
      description: 'Email',
      x_layout: '1/1/1/13',
    },
    password: {
      type: 'string',
      format: 'password',
      description: 'Mot de passe',
      x_layout: '2/1/2/13',
      minLength: 6,
    },
  },
  required: [
    'username',
    'password',
  ],
  additionalProperties: false,
};

/**
 * Login View
 * @returns {JSX.Element}
 */
function Login() {
  // Hooks
  const { t } = useTranslation();
  const { postAuthTokenMutation } = useParazol();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Services
  const {
    mutate: authTokenMutate,
    isLoading: isLoadingAuthToken,
    isSuccess: isSuccessAuthToken,
  } = postAuthTokenMutation;

  // Effects
  useEffect(() => {
    if (isSuccessAuthToken) {
      // Redirect to the home page or route from params
      const redirection = searchParams.get('redirect') || '/';
      navigate(redirection);
    }
  }, [isSuccessAuthToken]);

  // Handlers
  /**
   * Submit the form.
   * @param {object} data
   * @param {string} data.username
   * @param {string} data.password
   * @returns {void}
   */
  const onSubmit = (data) => {
    authTokenMutate({ ...data, grantType: 'password' });
  };

  return (
    <Box direction="column" gap="m" fullWidth>
      <Box direction="column" align="center" fullWidth className="margin-bottom-m">
        <Logo />
      </Box>
      <Box direction="column" gap="xs" align="center" fullWidth>
        <h1 className="h3-default font-weight-bold">
          {t('cortizol.login.title')}
        </h1>
        <span className="p1-default">
          {t('cortizol.login.description')}
        </span>
      </Box>

      <Box fullWidth>
        <Form
          name={FORM_NAME}
          className=""
          callbacks={{
            onSubmit,
          }}
          schema={SCHEMA}
          translations={{ formValidation: fr.cortizol.formValidation }}
        >
          {(formConfig, formInfo) => (
            <FormElements
              formConfig={formConfig}
              formTitle={formInfo.formName}
            />
          )}
        </Form>
      </Box>

      <Box direction="column" gap="xs" align="normal" fullWidth>
        <Button
          form={FORM_NAME}
          isSubmit
          isLoading={isLoadingAuthToken}
        >
          {t('cortizol.login.submit')}
        </Button>

        <Button color="noBackground">
          <Link to="/ask-reset-password">
            {t('cortizol.login.forgotPassword')}
          </Link>
        </Button>
      </Box>
    </Box>
  );
}

export default Login;
